import { zodResolver } from '@hookform/resolvers/zod';
import { showErrorNotification, showNotification } from 'components/ShowNotification';
import { SkillsMemberModel } from 'generated-types/skills/member';
import { useSkillsMemberHook } from 'hooks/skills/useSkillsMember';
import { useProfile, useMyExternalProfile } from 'hooks/useProfile';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FlexboxGrid, Panel } from 'rsuite';
import { EditProfileForm, EditProfileFormType } from 'schema/skills/profile';
import { isExternalRouter } from 'auth/cognito';
import { useResumeProfilesHook } from 'hooks/skills/useResumeProfiles';
import { ResumeProfileModel } from 'generated-types/skills/resume_profile';
import ProfileEditForm from './ProfileEditForm';
import ProfileModeButtons from './ProfileModeButtons';
import ExternalProfilePicture from './ExternalProfilePicture';
import ProfileInfo from './ProfileInfo';

export const isExtUserId = (member_id: number) => {
    if (member_id > 1000000) {
        return true;
    } else {
        return false;
    }
};

export const Profile = ({
    member,
    editAllowed,
    selectedResumeProfile,
    setSelectedResumeProfile,
    resumeProfiles,
}: {
    member: SkillsMemberModel;
    editAllowed: boolean;
    selectedResumeProfile: ResumeProfileModel | undefined;
    setSelectedResumeProfile: Dispatch<SetStateAction<ResumeProfileModel | undefined>>;
    resumeProfiles: ResumeProfileModel[];
}) => {
    const { user } = isExternalRouter ? useMyExternalProfile() : useProfile();
    const useSkillsMembers = useSkillsMemberHook(isExternalRouter);
    const { updateSkillsMember, refetch: refetchSkillsMember } = useSkillsMembers({
        enabled: false,
        member_id: user?.id,
    });

    const resumeProfilesHook = useResumeProfilesHook(
        user?.id,
        selectedResumeProfile?.resume_profile_id,
        isExternalRouter,
    );
    const { updateResumeProfile, refetchResumeProfiles } = resumeProfilesHook;

    const [editMode, setEditMode] = useState(false);

    const {
        formState: { errors },
        watch,
        control,
        handleSubmit,
        reset,
    } = useForm<EditProfileFormType>({
        resolver: zodResolver(EditProfileForm),
    });
    const watchFields = watch();

    useEffect(() => {
        if (selectedResumeProfile && editMode) {
            reset({
                headline: selectedResumeProfile.headline || '',
                about: selectedResumeProfile.about || '',
            });
        }
    }, [selectedResumeProfile, editMode, reset]);

    const onSave: SubmitHandler<EditProfileFormType> = data => {
        const { about, headline } = data;

        updateResumeProfile.mutate(
            {
                about: about ?? '',
                headline: headline ?? '',
                is_primary: resumeProfiles.length === 1 ? true : false,
            },
            {
                onSuccess: data => {
                    // console.log('onSave data', data);
                    setSelectedResumeProfile({
                        headline: data.headline,
                        about: data.about,
                        is_primary: false,
                        resume_profile_id: data.resume_profile_id,
                        created_at: data.created_at,
                        member_id: data.member_id,
                        skill_ids: [],
                    });
                    reset({
                        headline: data.headline,
                        about: data.about,
                    });
                    showNotification({ header: 'Profile updated successfully' });
                    setEditMode(false);
                    refetchSkillsMember();
                    refetchResumeProfiles();
                },
                onError: error => {
                    showErrorNotification(error);
                },
            },
        );
    };

    const onClose = () => {
        setEditMode(false);
        if (selectedResumeProfile) {
            reset({
                headline: selectedResumeProfile.headline || '',
                about: selectedResumeProfile.about || '',
            });
        }
    };

    return (
        <Panel bordered style={{ marginBottom: 20 }}>
            <form onSubmit={handleSubmit(onSave)}>
                <FlexboxGrid align="middle" justify="space-between">
                    {isExtUserId(member.member_id) ? (
                        <ExternalProfilePicture member={member} editAllowed={editAllowed} />
                    ) : (
                        <FlexboxGrid.Item colspan={3} style={{ textAlign: 'center' }}>
                            <img
                                src={member.image_url}
                                style={{ maxWidth: 120, width: '100%', borderRadius: '50%' }}
                            />
                        </FlexboxGrid.Item>
                    )}
                    <FlexboxGrid.Item colspan={editAllowed ? 17 : 21} style={{ paddingLeft: 20 }}>
                        <h1>
                            {member.firstname} {member.lastname}
                        </h1>
                        {editMode && selectedResumeProfile ? (
                            <ProfileEditForm
                                selectedResumeProfile={selectedResumeProfile}
                                member={member}
                                control={control}
                                watchFields={watchFields}
                                errors={errors}
                            />
                        ) : (
                            <ProfileInfo
                                selectedResumeProfile={selectedResumeProfile}
                                editAllowed={editAllowed}
                                setEditMode={setEditMode}
                            />
                        )}
                    </FlexboxGrid.Item>
                    <ProfileModeButtons
                        selectedResumeProfile={selectedResumeProfile}
                        editAllowed={editAllowed}
                        editMode={editMode}
                        isLoading={updateSkillsMember.isLoading}
                        onClose={onClose}
                        setEditMode={setEditMode}
                    />
                </FlexboxGrid>
            </form>
        </Panel>
    );
};
